import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router';
import {
  Badge,
  BadgeProgress,
  TIER_FROM_LEVEL,
  TIER_COLORS,
  TIER_GLOW_COLORS,
  UserBadge,
} from 'types/interfaces/badge';
import BadgeIcon from 'components/BadgeIcon/BadgeIcon';
import BadgeTooltip from 'components/BadgeTooltip/BadgeTooltip';

const CRITERIA_TO_PROGRESS_KEY: Record<string, keyof BadgeProgress> = {
  cards_sent: 'cards_sent',
  cards_received: 'cards_received',
  max_package_value_sent: 'max_package_value_sent',
  unique_trade_partners: 'unique_trade_partners',
  total_trade_value: 'total_trade_value',
  consecutive_trading_days: 'consecutive_trading_days',
  avg_ship_time_days: 'avg_ship_time_days',
  total_trades: 'total_trades',
};

interface BadgeCategorySectionProps {
  category: string;
  badges: Badge[];
  earnedMap: Map<number, UserBadge>;
  progress: BadgeProgress | undefined;
  isOwnProfile: boolean;
  isSelected: (badgeId: number) => boolean;
  onShowcase: (badgeId: number) => void;
  animationDelay: number;
}

const BadgeCategorySection: React.FC<BadgeCategorySectionProps> = ({
  category,
  badges,
  earnedMap,
  progress,
  isOwnProfile,
  isSelected,
  onShowcase,
  animationDelay,
}) => {
  const [hoveredBadge, setHoveredBadge] = useState<{ badge: Badge; earned: boolean; earnedAt?: string } | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);
  const [progressWidth, setProgressWidth] = useState(0);

  const sortedBadges = [...badges].sort((a, b) => a.level - b.level);
  const earnedCount = badges.filter((b) => earnedMap.has(b.id)).length;
  const totalCount = badges.length;

  // Find highest tier earned for progress bar color
  const highestEarnedTier = badges
    .filter((b) => earnedMap.has(b.id))
    .reduce((max, b) => (b.level > max ? b.level : max), 0);
  const progressColor = highestEarnedTier > 0
    ? TIER_COLORS[TIER_FROM_LEVEL[highestEarnedTier]]
    : '#7b5cff';

  // Animate progress bar on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setProgressWidth(totalCount > 0 ? (earnedCount / totalCount) * 100 : 0);
    }, animationDelay + 100);
    return () => clearTimeout(timer);
  }, [earnedCount, totalCount, animationDelay]);

  const handleBadgeMouseEnter = (e: React.MouseEvent, badge: Badge) => {
    const ub = earnedMap.get(badge.id);
    hoverTimeout.current = setTimeout(() => {
      setHoveredBadge({ badge, earned: !!ub, earnedAt: ub?.earnedAt });
      setTooltipPosition({ x: e.clientX, y: e.clientY });
    }, 200);
  };

  const handleBadgeMouseMove = (e: React.MouseEvent) => {
    if (hoveredBadge) {
      setTooltipPosition({ x: e.clientX, y: e.clientY });
    }
  };

  const handleBadgeMouseLeave = () => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
    setHoveredBadge(null);
  };

  const getProgressForBadge = (badge: Badge): { current: number; target: number } | null => {
    if (!progress || !badge.criteriaValue) return null;
    const key = CRITERIA_TO_PROGRESS_KEY[badge.criteria];
    if (!key) return null;
    const current = progress[key];
    if (current === null || current === undefined) return null;
    return { current: Number(current), target: badge.criteriaValue };
  };

  return (
    <div
      className="badge-category"
      style={{ animationDelay: `${animationDelay}ms` }}
    >
      <div className="badge-category__header">
        <div className="badge-category__header-top">
          <h3 className="badge-category__name">{category}</h3>
          <span className="badge-category__count">{earnedCount} of {totalCount} earned</span>
        </div>
        <div className="badge-category__progress-bar">
          <div
            className="badge-category__progress-fill"
            style={{
              width: `${progressWidth}%`,
              backgroundColor: progressColor,
            }}
          />
        </div>
      </div>

      <div className="badge-category__grid">
        {sortedBadges.map((badge, badgeIndex) => {
          const ub = earnedMap.get(badge.id);
          const earned = !!ub;
          const tier = TIER_FROM_LEVEL[badge.level] || 'bronze';
          const showcased = isSelected(badge.id);
          const badgeProgress = !earned && isOwnProfile ? getProgressForBadge(badge) : null;
          const glowColor = TIER_GLOW_COLORS[tier];

          return (
            <div
              key={badge.id}
              className={`badge-card ${earned ? `badge-card--earned badge-card--tier-${tier}` : 'badge-card--unearned'} ${showcased ? 'badge-card--showcased' : ''}`}
              style={{
                animationDelay: `${animationDelay + (badgeIndex * 30)}ms`,
                '--badge-glow': earned ? glowColor : 'transparent',
                '--card-index': badgeIndex,
              } as React.CSSProperties}
              onMouseEnter={(e) => handleBadgeMouseEnter(e, badge)}
              onMouseMove={handleBadgeMouseMove}
              onMouseLeave={handleBadgeMouseLeave}
            >
              <div className="badge-card__icon">
                <BadgeIcon
                  icon={badge.icon}
                  shape={badge.shape}
                  tier={tier}
                  size={64}
                  earned={earned}
                />
              </div>

              <div
                className="badge-card__title"
                style={{ color: earned ? TIER_COLORS[tier] : undefined }}
              >
                {badge.title}
              </div>
              <div className="badge-card__subtitle">{badge.subtitle}</div>

              {earned && ub?.earnedAt && (
                <div className="badge-card__earned-date">
                  Earned {new Date(ub.earnedAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </div>
              )}

              {!earned && badge.isCompetitive && badge.currentHolder && (
                <div className="badge-card__holder">
                  {badge.currentHolder.avatarUrl && (
                    <img
                      src={badge.currentHolder.avatarUrl}
                      alt=""
                      className="badge-card__holder-avatar"
                    />
                  )}
                  <Link
                    to={`/profile/u/${badge.currentHolder.username.toLowerCase()}`}
                    className="badge-card__holder-name"
                  >
                    {badge.currentHolder.username}
                  </Link>
                </div>
              )}

              {!earned && badge.isCompetitive && !badge.currentHolder && (
                <div className="badge-card__holder badge-card__holder--unclaimed">
                  Unclaimed
                </div>
              )}

              {/* Progress bar for unearned badges (own profile) */}
              {badgeProgress && (
                <div className="badge-card__progress">
                  <div className="badge-card__progress-bar">
                    <div
                      className="badge-card__progress-fill"
                      style={{
                        width: `${Math.min(100, (badgeProgress.current / badgeProgress.target) * 100)}%`,
                      }}
                    />
                  </div>
                  <div className="badge-card__progress-text">
                    {badgeProgress.current.toLocaleString()} / {badgeProgress.target.toLocaleString()}
                  </div>
                </div>
              )}

              {/* Showcase button (own profile, earned) */}
              {isOwnProfile && earned && (
                <button
                  className={`badge-card__showcase-btn ${showcased ? 'badge-card__showcase-btn--active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowcase(badge.id);
                  }}
                >
                  {showcased ? (
                    <>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Showcased
                    </>
                  ) : (
                    'Showcase'
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {hoveredBadge && (
        <BadgeTooltip
          badge={hoveredBadge.badge}
          earned={hoveredBadge.earned}
          earnedAt={hoveredBadge.earnedAt}
          position={tooltipPosition}
        />
      )}
    </div>
  );
};

export default BadgeCategorySection;
